import type { HotelSummary, Money } from './api/types';

export type ComparisonSupplier = 'Booking.com' | 'Expedia.com' | 'Agoda.com';

export interface SupplierQuote {
  supplier: ComparisonSupplier;
  nightlyPrice: Money;
}

export interface PriceIntelligencePreview {
  source: 'development-fixture';
  publicPrice: Money;
  supplierQuotes: SupplierQuote[];
  savingAmount: Money;
  savingPercent: number;
  isGreatDeal: boolean;
}

const SUPPLIERS: ComparisonSupplier[] = ['Booking.com', 'Expedia.com', 'Agoda.com'];

function stableSeed(value: string) {
  return [...value].reduce((total, character) => total + character.charCodeAt(0), 0);
}

function money(amount: number, currencyCode: string): Money {
  return { amount: amount.toFixed(2), currencyCode };
}

/**
 * UI-only fixture for the price-intelligence contract that is still being built.
 * Keep this boundary explicit: replace this function with the comparison API adapter,
 * without changing HotelRow or HotelCard.
 */
export function previewPriceIntelligence(hotel: HotelSummary): PriceIntelligencePreview | null {
  // These prices are invented, so customers must never see them: every
  // comparison (hotel price check, compare-all drawer, hotel cards, city deals)
  // stays hidden until a real price feed replaces this fixture. Set
  // EXPO_PUBLIC_PRICE_COMPARISON_FIXTURE=true locally to work on those layouts.
  if (process.env.EXPO_PUBLIC_PRICE_COMPARISON_FIXTURE !== 'true') return null;

  const memberPrice = Number(hotel.lowestPrice.amount);
  if (!Number.isFinite(memberPrice) || memberPrice <= 0) return null;

  const seed = stableSeed(hotel.id);
  const basePremium = [0.09, 0.14, 0.2][seed % 3];
  const supplierQuotes = SUPPLIERS.map((supplier, index) => ({
    supplier,
    nightlyPrice: money(memberPrice * (1 + basePremium + index * 0.035), hotel.lowestPrice.currencyCode),
  }));
  const publicAmount = Math.min(...supplierQuotes.map((quote) => Number(quote.nightlyPrice.amount)));
  const saving = Math.max(0, publicAmount - memberPrice);
  const savingPercent = publicAmount > 0 ? Math.round((saving / publicAmount) * 100) : 0;

  return {
    source: 'development-fixture',
    publicPrice: money(publicAmount, hotel.lowestPrice.currencyCode),
    supplierQuotes,
    savingAmount: money(saving, hotel.lowestPrice.currencyCode),
    savingPercent,
    isGreatDeal: savingPercent > 10,
  };
}
